/**
 * Client for the DeckorateAPI "use slide templates" endpoints — the
 * reviewed-layout path, both plain JSON (no SSE):
 *
 *   1. POST /v1/deckorate/slides/create-slide-outline
 *        topic/document -> slide briefs, each with a RANKED template pick
 *        (recommended + alternatives). One LLM call + local ranking, so the
 *        client gets the whole outline in one response and can let the user
 *        accept or override each layout before anything is built.
 *   2. POST /v1/deckorate/slides/create-slide-template
 *        the same outline sent back WITH the chosen template per slide ->
 *        a per-slide MappingPlan (`placeholder_assignments`). This does NOT
 *        return a .pptx: the add-in fetches the chosen template's own .pptx
 *        and patches the plan into it (see service/templateAssembler.ts).
 *
 * Sibling of slideGenerationApi.ts (the AI-freehand SSE endpoint) — same
 * service, same auth story: callers pass the shared `AuthorizedFetch` from
 * `useDeckorateHttp()`, so this client only adds the request-shape headers.
 *
 * FORWARD COMPATIBILITY — TEMPLATE BLOB LOCATION
 * Today a fit result identifies its template only by catalog id
 * ("standard-layouts/Bio_Layout.pptx#1") + `template_slide_index`, and the
 * add-in resolves the actual .pptx through the template library's own
 * directory SAS (service/templateCatalogApi.ts). The backend team is adding
 * an explicit blob location (container / path / filename / slide number) to
 * this response so the client no longer has to resolve it. `TemplateBlobRef`
 * below parses that leniently — several likely field spellings, all optional
 * — so the richer response is picked up as soon as it ships without a
 * further change here, and `templateBlobRef()` is what the assembler asks
 * for its source file.
 */

import type { AuthorizedFetch } from "../../../services/deckorateApi";
import {
  SlideApiError,
  type GeneratePresentationRequest,
  type IntentTag,
  type SlideBrief,
} from "./slideGenerationApi";

const OUTLINE_PATH = "/v1/deckorate/slides/create-slide-outline";
const FIT_PATH = "/v1/deckorate/slides/create-slide-template";

/** The templates path is live — this replaces the old placeholder flag. */
export const TEMPLATES_API_AVAILABLE = true;

/* ── POST /create-slide-outline ──────────────────── */

/** One ranked template candidate for a slide (models/create_slides/outline.py). */
export interface TemplateMatch {
  id: string;
  name?: string | null;
  layout_class?: string | null;
  /** 1-based slide index within the template .pptx, parsed from the id's "#N". */
  slide_index?: number | null;
  score: number;
}

/** A brief plus its template match. `covered=false` → the ranker found nothing
 *  and the client MUST force a manual pick before building. */
export interface PlannedSlide extends SlideBrief {
  recommended_category?: string | null;
  recommended_template?: TemplateMatch | null;
  alternatives?: TemplateMatch[];
  covered?: boolean;
}

export interface OutlineCoverage {
  total: number;
  covered: number;
  uncovered: Array<{ slide_idx?: number; title?: string }>;
}

export interface OutlineResponse {
  request_id: string;
  title: string;
  slide_count: number;
  slides: PlannedSlide[];
  coverage?: OutlineCoverage | null;
  meta?: { request_id: string; model_id: string; source: string; processing_ms?: number } | null;
}

/* ── POST /create-slide-template ─────────────────── */

/** "generic" = the user picked the layout for its look, so restructure freely
 *  (the default for this flow). "preserve" = auto-matched, keep close. */
export type FitMode = "generic" | "preserve";

/** One outline brief plus the template chosen for it. */
export interface SlideFitInput extends SlideBrief {
  template_id: string;
  fit_mode?: FitMode;
}

export interface FitRequest {
  request_id?: string;
  fit_mode?: FitMode;
  slides: SlideFitInput[];
}

/** One run of text inside a rich paragraph — carries the emphasis the fitter
 *  wants preserved on the rewritten phrase. */
export interface RichRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface RichParagraph {
  runs: RichRun[];
  hasBullet?: boolean;
  level?: number;
}

/**
 * What the fitter assigns to ONE slot. Keys of `placeholder_assignments` are
 * "<role>#<idx>" in the reading order the slot manifest derived — see
 * service/templateAssembler.ts, which re-derives the identical order over the
 * same signature placeholders to resolve each key to a real shape.
 *
 * `action: "leave_blank"` means the template's own default text stays put, so
 * a null/absent `text` is NOT an error.
 */
export interface PlaceholderAssignment {
  sourceRef?: string;
  action: "copy" | "rewrite" | "split" | "leave_blank";
  text?: string | null;
  richBullets?: RichParagraph[];
}

export interface NonTextSlot {
  id: string;
  role?: string | null;
  kind?: string | null;
  name?: string | null;
}

/**
 * Where a template's .pptx actually lives. Every field is optional: the
 * current backend sends none of them (the assembler falls back to resolving
 * the file through the template library's directory SAS), and the agreed
 * richer response will send container/path/filename/slide number. See this
 * module's header.
 */
export interface TemplateBlobRef {
  /**
   * A ready-to-fetch SAS URL for the .pptx. Preferred over every other field
   * when present: it needs no directory-SAS rebuild and so isn’t limited to
   * the one library the add-in can mint a token for. This is the shape
   * DeckorateAPI’s own /content/blob-sas already returns (`sas_url`), so it is
   * the most likely field on the richer per-slide response.
   */
  readonly sasUrl?: string;
  readonly blobContainer?: string;
  /** Container-relative path to the .pptx, when the backend supplies one. */
  readonly blobPath?: string;
  /** Bare filename, when that's all that's supplied (e.g. "Bio_Layout.pptx"). */
  readonly fileName?: string;
  /** 1-based slide number INSIDE that .pptx. */
  readonly slideNumber?: number;
}

export interface SlideFitResult {
  slide_idx: number;
  title: string;
  intent_tag: IntentTag;
  template_id: string;
  /** 1-based slide number inside the template .pptx. Distinct from
   *  `slide_idx`, which is the slide's position in the generated deck. */
  template_slide_index?: number | null;
  status: "ok" | "failed";
  placeholder_assignments?: Record<string, PlaceholderAssignment>;
  non_text_slots?: NonTextSlot[];
  rationale?: string;
  dropped_source_refs?: string[];
  capacity_violations?: Array<Record<string, unknown>>;
  error?: string | null;
}

export interface FitResponse {
  request_id: string;
  slide_count: number;
  completed: number;
  failed: number;
  slides: SlideFitResult[];
  meta?: { request_id: string; model_id: string; source: string; processing_ms?: number } | null;
}

/* ── Blob-location extraction (lenient, see module header) ── */

function firstString(source: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return undefined;
}

function firstPositiveInt(source: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && Number.isInteger(value) && value > 0) return value;
  }
  return undefined;
}

/**
 * Reads whatever blob location a fit result carries. Returns an object with
 * every field undefined when the response predates the richer contract —
 * callers treat that as "resolve it from the catalog instead", not an error.
 *
 * Nested `blob`/`template`/`source` objects are checked as well as the flat
 * top level, since the shape isn't finalised yet.
 */
export function templateBlobRef(result: SlideFitResult): TemplateBlobRef {
  const raw = result as unknown as Record<string, unknown>;
  const nestedKeys = ["blob", "template", "source", "template_blob"];
  const scopes: Record<string, unknown>[] = [raw];
  for (const key of nestedKeys) {
    const nested = raw[key];
    if (typeof nested === "object" && nested !== null) scopes.push(nested as Record<string, unknown>);
  }

  let sasUrl: string | undefined;
  let blobContainer: string | undefined;
  let blobPath: string | undefined;
  let fileName: string | undefined;
  let slideNumber: number | undefined;

  for (const scope of scopes) {
    sasUrl ??= firstString(scope, "sas_url", "sasUrl");
    blobContainer ??= firstString(scope, "blob_container", "blobContainer", "container");
    blobPath ??= firstString(scope, "blob_path", "blobPath", "blob_name", "blobName", "path");
    fileName ??= firstString(scope, "file_name", "fileName", "pptx_file", "pptxFile", "filename");
    slideNumber ??= firstPositiveInt(scope, "slide_number", "slideNumber");
  }

  // `template_slide_index` is the established field for the slide inside the
  // template file, so it wins over anything sniffed above.
  const templateSlideIndex = result.template_slide_index;
  if (typeof templateSlideIndex === "number" && templateSlideIndex > 0) slideNumber = templateSlideIndex;

  return {
    ...(sasUrl ? { sasUrl } : {}),
    ...(blobContainer ? { blobContainer } : {}),
    ...(blobPath ? { blobPath } : {}),
    ...(fileName ? { fileName } : {}),
    ...(slideNumber ? { slideNumber } : {}),
  };
}

/* ── Transport ───────────────────────────────────── */

export interface TemplatesApiOptions {
  /** DeckorateAPI base URL (config.deckorateApiBaseUrl). */
  readonly baseUrl: string;
  /** Shared authorised fetch (bearer + APIM key + 401 refresh). */
  readonly authorizedFetch: AuthorizedFetch;
  readonly signal?: AbortSignal;
}

/** Throws SlideApiError for a non-2xx response, parsing the ErrorResponse envelope. */
async function throwForHttpError(res: Response, label: string): Promise<never> {
  let code = `HTTP_${res.status}`;
  let message = `${label} failed (${res.status})`;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string }; detail?: unknown };
    if (body.error?.code) code = body.error.code;
    if (body.error?.message) {
      message = body.error.message;
    } else if (body.detail !== undefined) {
      // FastAPI validation errors come back as `detail`, not the
      // ErrorResponse envelope — surface something specific rather than a
      // bare status code.
      message = `${label} was rejected: ${JSON.stringify(body.detail)}`;
    }
  } catch {
    /* non-JSON error body */
  }
  console.error(`[create-slide] ${label} failed (${res.status} ${code}):`, message);
  throw new SlideApiError(code, message, res.status);
}

async function postJson<T>(path: string, body: unknown, label: string, opts: TemplatesApiOptions): Promise<T> {
  const res = await opts.authorizedFetch(`${opts.baseUrl.replace(/\/$/, "")}${path}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (!res.ok) await throwForHttpError(res, label);
  return (await res.json()) as T;
}

/**
 * POST /create-slide-outline — the deck outline with a ranked template per
 * slide. Takes the same request body as the freehand endpoint (topic or
 * blob-stored document); the template ranking is added server-side.
 */
export async function fetchSlideOutline(
  req: GeneratePresentationRequest,
  opts: TemplatesApiOptions,
): Promise<OutlineResponse> {
  const outline = await postJson<OutlineResponse>(OUTLINE_PATH, req, "Slide outline", opts);
  // Contract guard: an outline with no `slides` array would otherwise surface
  // as an empty picker that silently generates nothing.
  if (!outline || !Array.isArray(outline.slides)) {
    throw new SlideApiError("OUTLINE_INVALID", "The server returned an invalid slide outline.");
  }
  return outline;
}

/**
 * POST /create-slide-template — fit the outline into the chosen templates.
 * Per-slide failures come back as `status: "failed"` on that slide, not as a
 * rejected promise, so the deck still builds around one bad slide.
 */
export async function fitSlidesToTemplates(req: FitRequest, opts: TemplatesApiOptions): Promise<FitResponse> {
  const fit = await postJson<FitResponse>(FIT_PATH, req, "Slide template fit", opts);
  if (!fit || !Array.isArray(fit.slides)) {
    throw new SlideApiError("FIT_INVALID", "The server returned an invalid template fit plan.");
  }
  return fit;
}
